import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BookOpen, Braces, Search } from 'lucide-react';
import { useKbArticlesInfinite } from '@/api/kb';
import { cn } from '@/lib/cn';
import { useDebounced } from './useDebounced';

/** Variables agents can type; the server fills them per customer when the reply is sent. */
const REPLY_VARIABLES: { key: string; label: string }[] = [
  { key: '{{first_name}}', label: 'First name' },
  { key: '{{name}}', label: 'Full name' },
  { key: '{{email}}', label: 'Email' },
  { key: '{{plan}}', label: 'Plan' },
  { key: '{{city}}', label: 'City' },
  { key: '{{ticket_number}}', label: 'Ticket number' },
  { key: '{{agent_first_name}}', label: 'Your first name' },
  { key: '{{first_name | there}}', label: 'First name, or “there”' },
];

function Popover({
  open,
  onClose,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node))
        onClose();
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      ref={ref}
      className={cn(
        'shadow-pop absolute bottom-full z-20 mb-2 rounded-xl border border-slate-200 bg-white p-1.5',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ComposerExtras({
  onInsert,
  onPickArticle,
  allowArticles,
}: {
  onInsert: (text: string) => void;
  onPickArticle: (a: { id: string; title: string; excerpt: string }) => void;
  allowArticles: boolean;
}) {
  const [open, setOpen] = useState<'vars' | 'articles' | null>(null);
  const [q, setQ] = useState('');
  const debounced = useDebounced(q.trim(), 250);
  const articles = useKbArticlesInfinite({ status: 'published', q: debounced || undefined });
  const rows = open === 'articles' ? (articles.data?.pages[0]?.data ?? []).slice(0, 6) : [];
  const btn =
    'flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800';

  return (
    <div className="relative flex items-center gap-0.5">
      <button
        type="button"
        className={btn}
        onClick={() => setOpen(open === 'vars' ? null : 'vars')}
        aria-expanded={open === 'vars'}
      >
        <Braces className="h-3.5 w-3.5" /> Variables
      </button>
      {allowArticles && (
        <button
          type="button"
          className={btn}
          onClick={() => setOpen(open === 'articles' ? null : 'articles')}
          aria-expanded={open === 'articles'}
        >
          <BookOpen className="h-3.5 w-3.5" /> Article
        </button>
      )}

      <Popover open={open === 'vars'} onClose={() => setOpen(null)} className="left-0 w-64">
        <p className="px-2 pt-1 pb-1.5 text-[11px] text-slate-400">Filled in for this customer when sent</p>
        {REPLY_VARIABLES.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => {
              onInsert(v.key);
              setOpen(null);
            }}
            className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-50"
          >
            <span className="text-slate-700">{v.label}</span>
            <code className="text-brand-700 font-mono text-[11px]">{v.key}</code>
          </button>
        ))}
      </Popover>

      <Popover open={open === 'articles'} onClose={() => setOpen(null)} className="left-0 w-80">
        <div className="relative mb-1">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            autoFocus
            aria-label="Search articles"
            placeholder="Search help articles…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-8 w-full rounded-lg border border-slate-200 pr-2 pl-8 text-sm focus:outline-none"
          />
        </div>
        {rows.length === 0 ? (
          <p className="px-2 py-3 text-center text-xs text-slate-400">
            {articles.isPending ? 'Loading…' : 'No published articles match'}
          </p>
        ) : (
          rows.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => {
                onPickArticle({ id: a.id, title: a.title, excerpt: a.excerpt });
                setOpen(null);
              }}
              className="block w-full rounded-lg px-2 py-1.5 text-left hover:bg-slate-50"
            >
              <span className="block truncate text-sm font-medium text-slate-800">{a.title}</span>
              <span className="block truncate text-xs text-slate-500">{a.excerpt}</span>
            </button>
          ))
        )}
      </Popover>
    </div>
  );
}
